/**
 * Shared client-side state for the "Get a summary" capture experience.
 *
 * Consumers read/write these keys so that once a visitor has left their
 * details (or dismissed the prompt) they aren't nagged again. Keeping the
 * keys in one place stops the flows drifting.
 */

export const SUMMARY_CAPTURE_STORAGE_KEY = "hg_email_captured";
export const SUMMARY_CAPTURE_DISMISS_KEY = "hg_email_dismissed";

/** Re-show the prompt this long after a dismissal (30 days). */
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Forget a captured email this long after submission (7 days). */
const CAPTURED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface CapturedSummaryEntry {
  email: string;
  capturedAt: number;
}

/**
 * Returns the stored capture while it is within the TTL, otherwise removes it.
 * Entries without a timestamp (legacy plain-email values) are treated as expired.
 */
const readCapturedEntry = (): CapturedSummaryEntry | null => {
  const raw = localStorage.getItem(SUMMARY_CAPTURE_STORAGE_KEY);
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw) as Partial<CapturedSummaryEntry>;
    if (
      typeof entry?.email === "string" &&
      typeof entry.capturedAt === "number" &&
      Date.now() - entry.capturedAt < CAPTURED_TTL_MS
    ) {
      return { email: entry.email, capturedAt: entry.capturedAt };
    }
  } catch {
    /* legacy or malformed value — fall through to removal */
  }
  localStorage.removeItem(SUMMARY_CAPTURE_STORAGE_KEY);
  return null;
};

/** True if the visitor submitted within the TTL, or dismissed within the TTL window. */
export const shouldHideSummaryCapture = (): boolean => {
  try {
    if (readCapturedEntry()) return true;
    const dismissed = localStorage.getItem(SUMMARY_CAPTURE_DISMISS_KEY);
    if (dismissed) {
      const ts = parseInt(dismissed, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_TTL_MS) return true;
    }
  } catch {
    // Private mode / storage disabled — fail open so capture still works.
  }
  return false;
};

/** Returns the email captured on this device within the TTL, if any. */
export const getCapturedSummaryEmail = (): string | null => {
  try {
    return readCapturedEntry()?.email ?? null;
  } catch {
    return null;
  }
};

/** Remember that the visitor left their email (suppresses both surfaces for the TTL). */
export const markSummaryCaptured = (email: string): void => {
  try {
    const entry: CapturedSummaryEntry = { email, capturedAt: Date.now() };
    localStorage.setItem(SUMMARY_CAPTURE_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    /* no-op */
  }
};

/** Remember a dismissal so we don't re-prompt for the TTL window. */
export const markSummaryDismissed = (): void => {
  try {
    localStorage.setItem(SUMMARY_CAPTURE_DISMISS_KEY, String(Date.now()));
  } catch {
    /* no-op */
  }
};
